import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/sharp-solid-svg-icons';
import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '@/components/button';
import { OverflowMenuItem } from '@/components/dropDownButton/menuItem';
import { GLOBAL_TOOLTIP } from '@/constants/constants';
import useBreakpoints, { BreakpointsEnum } from '@/hooks/useBreakpoint';
import { useAuth } from '@/providers/auth-provider';

import { useNavigationContext } from '../provider';
import { ReportDropDownButton } from './reportDropDownButton';

interface UtilButtonsProps {
  shareDisabled: boolean;
  shareDisabledTooltipText: string;
  onShare: () => void;
  onShareDisabledClick: () => void;
  isCompReportButtonDisabled: boolean;
  onCompReportClick: () => void;
  onCompReportDisabledClick: () => void;
  onInsightReportClick: () => void;
  insightReportButtonDisabled: boolean;
}

export const UtilButtons: FC<UtilButtonsProps> = ({
  shareDisabled,
  shareDisabledTooltipText,
  onShare,
  onShareDisabledClick,
  isCompReportButtonDisabled,
  onCompReportClick,
  onCompReportDisabledClick,
  onInsightReportClick,
  insightReportButtonDisabled,
}) => {
  const { breakpoint } = useBreakpoints();
  const isMobile = breakpoint.down(BreakpointsEnum.SM);

  const { userCanMakeQuickMarketAnalysisReports, userCanMakeAerialMapReports } =
    useAuth();

  const { aerialMapIsOpen, openAerialMap } = useNavigationContext();

  const compReportDisabledText = useMemo(() => {
    return userCanMakeQuickMarketAnalysisReports
      ? 'Add sales using the Sold Land layer.'
      : 'Upgrade to access.';
  }, [userCanMakeQuickMarketAnalysisReports]);

  const groupOptions = useMemo(() => {
    return [
      [
        {
          disabled: insightReportButtonDisabled,
          disabledText: 'Select or create a boundary.',
          id: 'property-analysis',
          label: 'Property Analysis',
          onClick: onInsightReportClick,
          value: 'property-analysis',
        },
        {
          disabled: aerialMapIsOpen || !userCanMakeAerialMapReports,
          id: 'aerial-map',
          label: 'Aerial Map',
          onClick: openAerialMap,
          value: 'aerial-map',
        },
        {
          disabled: isCompReportButtonDisabled,
          disabledText: compReportDisabledText,
          id: 'market-analysis',
          label: 'Market Analysis',
          onClick: onCompReportClick,
          onDisabledClick: onCompReportDisabledClick,
          value: 'market-analysis',
        },
      ],
    ] as OverflowMenuItem[][];
  }, [
    aerialMapIsOpen,
    insightReportButtonDisabled,
    onInsightReportClick,
    userCanMakeAerialMapReports,
    openAerialMap,
    isCompReportButtonDisabled,
    compReportDisabledText,
    onCompReportClick,
    onCompReportDisabledClick,
  ]);

  return (
    <>
      <div
        className={isMobile ? 'h-[44px] w-[44px]' : 'w-full'}
        id="report-button"
      >
        <ReportDropDownButton groupOptions={groupOptions} isMobile={isMobile} />
      </div>

      <div
        className={isMobile ? 'h-[44px] w-[44px]' : 'w-full'}
        data-testid="share-export-button"
        data-tooltip-content={shareDisabledTooltipText}
        data-tooltip-id={GLOBAL_TOOLTIP}
      >
        <Button
          className={twMerge(
            'h-full w-full whitespace-nowrap text-gray-70 sm:h-[32px] sm:w-full sm:px-3',
            isMobile
              ? 'flex h-full w-full items-center justify-center p-0'
              : 'text-white'
          )}
          disabled={shareDisabled}
          size={ButtonSize.XS}
          variant={isMobile ? ButtonVariant.GHOST : ButtonVariant.PRIMARY}
          onClick={onShare}
          onDisabledClick={onShareDisabledClick}
        >
          <FontAwesomeIcon
            className={twMerge(isMobile ? '' : 'mr-2')}
            icon={faShare}
          />
          {!isMobile && 'Share/Embed'}
        </Button>
      </div>
    </>
  );
};
